import { FormLayout, Label, Select, TextInput } from '@kcftech/design-system';
import {
    AssetCategoryParameterType,
    IAssetCategoryParameterChild,
} from '@kcftech/prometheustypescriptapiclient';
import { AssetParameterBoolFormContent } from 'pages/Settings/AssetType/parameter_form/AssetParameterBoolFormContent';
import { AssetParameterDecimalFormContent } from 'pages/Settings/AssetType/parameter_form/AssetParameterDecimalFormContent';
import { AssetParameterIntegerFormContent } from 'pages/Settings/AssetType/parameter_form/AssetParameterIntegerFormContent';
import { AssetParameterSelectionFormContent } from 'pages/Settings/AssetType/parameter_form/AssetParameterSelectionFormContent';
import { AssetParameterTextFormContent } from 'pages/Settings/AssetType/parameter_form/AssetParameterTextFormContent';
import {
    AssetParameterFormData,
    AssetParameterFormInputProps,
} from 'pages/Settings/AssetType/parameter_form/types';
import { FunctionComponent, useEffect, useMemo } from 'react';
import { RegisterOptions, useWatch } from 'react-hook-form';
import { formattedParameterTypes } from 'utils/asset_classification';
import { Lookup } from 'utils/types';

const nameMaxLength = 100;
const codeMaxLength = 100;

const supportedTypes = [
    AssetCategoryParameterType.Text,
    AssetCategoryParameterType.Integer,
    AssetCategoryParameterType.Decimal,
    AssetCategoryParameterType.Bool,
    AssetCategoryParameterType.MonitoringPointId,
    AssetCategoryParameterType.Selection,
];

const parameterTypeOptions = supportedTypes.map((type) => ({
    title: formattedParameterTypes[type],
    value: type,
}));

const getNameRules = (
    siblingParameters: IAssetCategoryParameterChild[]
): RegisterOptions<AssetParameterFormData, 'name'> => ({
    validate: (value) => {
        const trimmedValue = value.trim();
        if (!trimmedValue) {
            return 'Parameter name is required';
        }

        if (siblingParameters.some((param) => param.Name === trimmedValue)) {
            return 'Parameter name already exists';
        }

        return undefined;
    },
    maxLength: {
        value: nameMaxLength,
        message: `Parameter name cannot exceed ${nameMaxLength} characters`,
    },
});

const getCodeRules = (
    siblingParameters: IAssetCategoryParameterChild[]
): RegisterOptions<AssetParameterFormData, 'code'> => ({
    validate: (value) => {
        const trimmedValue = value.trim();
        if (!trimmedValue) {
            return 'Parameter code is required';
        }

        if (siblingParameters.some((param) => param.Code === trimmedValue)) {
            return 'Parameter code already exists';
        }

        return undefined;
    },
    maxLength: {
        value: codeMaxLength,
        message: `Parameter code cannot exceed ${codeMaxLength} characters`,
    },
});

const typeRules: RegisterOptions<AssetParameterFormData, 'type'> = {
    required: 'Parameter type is required',
};

const parameterFormContentLookup: Lookup<
    AssetCategoryParameterType,
    FunctionComponent<AssetParameterFormInputProps>
> = {
    [AssetCategoryParameterType.Text]: AssetParameterTextFormContent,
    [AssetCategoryParameterType.Bool]: AssetParameterBoolFormContent,
    [AssetCategoryParameterType.Integer]: AssetParameterIntegerFormContent,
    [AssetCategoryParameterType.Decimal]: AssetParameterDecimalFormContent,
    [AssetCategoryParameterType.Selection]: AssetParameterSelectionFormContent,
    [AssetCategoryParameterType.MonitoringPointId]: () => <></>,
};

export type AssetParameterFormContentProps = AssetParameterFormInputProps &
    Readonly<{
        siblingParameters: IAssetCategoryParameterChild[];
    }>;

export function AssetParameterFormContent({
    form,
    parameter,
    siblingParameters,
    parentCategory,
}: AssetParameterFormContentProps) {
    const {
        register,
        formState: { errors },
        setValue,
    } = form;

    const type = useWatch({
        ...form,
        name: 'type',
    });

    const ParameterFormContent = parameterFormContentLookup[type];
    const nameRules = useMemo(() => getNameRules(siblingParameters), [siblingParameters]);
    const codeRules = useMemo(() => getCodeRules(siblingParameters), [siblingParameters]);

    useEffect(() => {
        setValue('name', parameter?.Name ?? '');
        setValue('code', parameter?.Code ?? '');
        setValue(
            'type',
            parameter?.CategoryParameterType ?? AssetCategoryParameterType.Text
        );
    }, [parameter, setValue]);

    return (
        <FormLayout>
            {parentCategory && (
                <Label label="Parent Category">
                    <div className="mt-5">{parentCategory.Name}</div>
                </Label>
            )}

            <TextInput
                required
                label="Name"
                placeholder="Enter a name for the asset category parameter"
                error={errors.name?.message}
                {...register('name', nameRules)}
            />

            <TextInput
                required
                label="Code"
                placeholder="Enter a code for the asset category parameter"
                error={errors.code?.message}
                {...register('code', codeRules)}
            />

            <Select
                ariaLabel="select asset category parameter type"
                required
                label="Type"
                error={errors.type?.message}
                options={parameterTypeOptions}
                {...register('type', typeRules)}
            />

            {
                // react-hook-form may return `undefined` for type on first render
                // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
                ParameterFormContent !== undefined && (
                    <ParameterFormContent
                        form={form}
                        parameter={parameter}
                        parentCategory={parentCategory}
                    />
                )
            }
        </FormLayout>
    );
}
